import ModalWarning from 'components/ui/ModalWarning';
import { ModalEscolhaPreliminarEtapa3 } from 'components/ModalEscolhaPreliminarEtapa3';
import { VotacaoEtapa3TutorialModal } from 'components/VotacaoEtapa3TutorialModal';
import { useAuth } from 'context/AuthContext';
import { useModal } from 'context/ModalContext/Modal.context';
import { useControlDate } from 'hooks/useControlDate';
import { useVotacaoEtapa3 } from 'hooks/useVotacaoEtapa3';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { IoCloudDownloadOutline, IoSearch, IoTimeOutline } from 'react-icons/io5';
import { useLocation } from 'react-router-dom';
import { buscarIniciativasEtapa3 } from 'services/iniciativa.service';
import { getTutorial } from 'services/tutorial.service';
import { getTutorialEtapa3IdForUser } from 'types/etapa3';
import { IniciativaFormatada } from 'types/iniciativa';
import { Tutorial } from 'types/tutorial';
import { PHASE_IDS } from 'utils/constants';
import { HeaderElbb } from '../HomeElbb/components';
import {
  CATEGORIAS_PARTICIPANTES_ETAPA3,
  CategoriasParticipantesEtapa3,
} from '../HomeElbb/components/CategoriasParticipantesEtapa3';
import { IniciativaCardEtapa3 as IniciativaCard } from '../HomePublic/components/IniciativaCardEtapa3';
import './style.scss';

const STAGE_TIMEZONE = 'America/Sao_Paulo';
const FALLBACK_START = new Date('2026-05-04T00:00:00-03:00');
const FALLBACK_END = new Date('2026-05-22T23:59:59-03:00');

const formatDateDayMonth = (date: Date) =>
  new Intl.DateTimeFormat('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    timeZone: STAGE_TIMEZONE,
  }).format(date);

function useCountdown(target: Date) {
  const [timeLeft, setTimeLeft] = useState(() => Math.max(0, target.getTime() - Date.now()));

  useEffect(() => {
    const interval = setInterval(() => {
      setTimeLeft(Math.max(0, target.getTime() - Date.now()));
    }, 1000);
    return () => clearInterval(interval);
  }, [target]);

  const totalSeconds = Math.floor(timeLeft / 1000);
  const days = Math.floor(totalSeconds / (3600 * 24));
  const hours = Math.floor((totalSeconds % (3600 * 24)) / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  const fmt = (n: number) => String(n).padStart(2, '0');

  if (days > 0) {
    return `${fmt(days)}d ${fmt(hours)}:${fmt(minutes)}:${fmt(seconds)}`;
  }
  return `${fmt(hours)}:${fmt(minutes)}:${fmt(seconds)}`;
}

export function VotacaoEtapa3() {
  const { user } = useAuth();
  const { showModal, closeModal } = useModal();
  const controlDate = useControlDate(PHASE_IDS.ETAPA_3_PREMIO);
  const location = useLocation();

  const [categoriaSelecionada, setCategoriaSelecionada] = useState<string>(() => {
    const fromState = (location.state as { categoria?: string } | null)?.categoria;
    const valido = CATEGORIAS_PARTICIPANTES_ETAPA3.some((c) => c.id === fromState);
    return valido ? fromState! : CATEGORIAS_PARTICIPANTES_ETAPA3[0].id;
  });
  const [busca, setBusca] = useState('');
  const [debouncedBusca, setDebouncedBusca] = useState('');
  const [todasIniciativas, setTodasIniciativas] = useState<IniciativaFormatada[]>([]);
  const [loadingIniciativas, setLoadingIniciativas] = useState(true);
  const [erroIniciativas, setErroIniciativas] = useState<string | null>(null);
  const [isExportingPdf, setIsExportingPdf] = useState(false);
  const [floatingDismissed, setFloatingDismissed] = useState<Record<string, boolean>>({});
  const [tutorialEtapa3, setTutorialEtapa3] = useState<Tutorial | null>(null);

  const {
    votos,
    limite,
    perfil,
    vpDestino,
    isReadOnly,
    statusPreliminar,
    preliminarSuperseded,
    preliminarBaseadoNoVp,
    preliminarVp,
    mostrarModalPreliminar,
    isLoadingRascunho,
    isSaving,
    isSubmitting,
    submitError,
    handleToggleVoto,
    handleEnviarVotos,
    handleEnviarParaAprovacao,
    aceitarPreliminar,
    recusarPreliminar,
    prontoParaEnvio,
    podeEnviar,
  } = useVotacaoEtapa3();

  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const periodo = useMemo(() => {
    const inicio = controlDate?.dataInicio ? new Date(controlDate.dataInicio) : FALLBACK_START;
    const fim = controlDate?.dataFim ? new Date(controlDate.dataFim) : FALLBACK_END;
    return { inicio, fim };
  }, [controlDate]);

  const countdown = useCountdown(periodo.fim);
  const startLabel = formatDateDayMonth(periodo.inicio);
  const endLabel = formatDateDayMonth(periodo.fim);

  const progresso = useMemo(() => {
    const total = periodo.fim.getTime() - periodo.inicio.getTime();
    const decorrido = Date.now() - periodo.inicio.getTime();
    if (total <= 0) return 0;
    return Math.min(Math.max((decorrido / total) * 100, 0), 100);
  }, [periodo]);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => setDebouncedBusca(busca.trim()), 350);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [busca]);

  useEffect(() => {
    setBusca('');
    setDebouncedBusca('');
  }, [categoriaSelecionada]);

  useEffect(() => {
    let canceled = false;
    if (!user?._id) return;
    getTutorial(getTutorialEtapa3IdForUser(user))
      .then((t) => { if (!canceled) setTutorialEtapa3(t ?? null); })
      .catch(() => { if (!canceled) setTutorialEtapa3(null); });
    return () => { canceled = true; };
  }, [user?._id]);

  useEffect(() => {
    let cancelled = false;
    async function carregar() {
      try {
        setLoadingIniciativas(true);
        setErroIniciativas(null);
        const items = await buscarIniciativasEtapa3(user?._id);
        if (cancelled) return;
        setTodasIniciativas(items);
      } catch {
        if (cancelled) return;
        setErroIniciativas('Não foi possível carregar as iniciativas. Tente novamente.');
        setTodasIniciativas([]);
      } finally {
        if (!cancelled) setLoadingIniciativas(false);
      }
    }
    carregar();
    return () => { cancelled = true; };
  }, [user?._id]);

  // Abre o modal de escolha do VP. O hook só seta mostrarModalPreliminar=true uma
  // vez (no mount); aceitarPreliminar/recusarPreliminar o voltam para false e o
  // guard abaixo evita reabertura. Não há closeModal no cleanup de propósito.
  useEffect(() => {
    if (!mostrarModalPreliminar) return;
    showModal(
      'preliminar-etapa3-escolha',
      <ModalEscolhaPreliminarEtapa3
        assessorNome={preliminarVp?.assessorNome}
        onManterVoto={() => {
          closeModal();
          recusarPreliminar();
        }}
        onCarregarVoto={() => {
          closeModal();
          aceitarPreliminar();
        }}
      />,
      'preliminar-etapa3-escolha-wrapper'
    );
  }, [
    mostrarModalPreliminar,
    showModal,
    closeModal,
    aceitarPreliminar,
    recusarPreliminar,
    preliminarVp,
  ]);

  const votosCategoriaAtual = votos[categoriaSelecionada]?.ids ?? [];
  const atingiuLimite = votosCategoriaAtual.length >= limite;

  const contadores = useMemo(() => {
    const map: Record<string, number> = {};
    CATEGORIAS_PARTICIPANTES_ETAPA3.forEach((cat) => {
      map[cat.id] = votos[cat.id]?.ids?.length || 0;
    });
    return map;
  }, [votos]);

  const iniciativas = useMemo(() => {
    const termo = debouncedBusca.toLowerCase();
    return todasIniciativas.filter(
      (i) => termo.length === 0 || i.titulo.toLowerCase().includes(termo)
    );
  }, [todasIniciativas, debouncedBusca]);

  const categoriasPendentes = useMemo(
    () => limite === 1
      ? CATEGORIAS_PARTICIPANTES_ETAPA3.filter((cat) => (contadores[cat.id] ?? 0) < 1)
      : [],
    [limite, contadores]
  );

  const podeClicarEnviar = limite === 1 ? podeEnviar : prontoParaEnvio;

  const categoriaInfo = useMemo(
    () => CATEGORIAS_PARTICIPANTES_ETAPA3.find((c) => c.id === categoriaSelecionada),
    [categoriaSelecionada]
  );

  const handleClickEnviar = useCallback(() => {
    if (limite === 1 && categoriasPendentes.length > 0) {
      showModal(
        'confirmar-envio-etapa3',
        <ModalWarning
          title="Categorias sem voto"
          description={
            <div className="py-3">
              <p className="mb-3">Você ainda não votou em todas as categorias. Deseja enviar mesmo assim?</p>
              <p className="font-semibold mb-1">Pendentes:</p>
              <ul className="list-disc pl-5 space-y-1">
                {categoriasPendentes.map((cat) => (
                  <li key={cat.id}>{cat.nome}</li>
                ))}
              </ul>
            </div>
          }
          btnText="Enviar mesmo assim"
          hasBackButton
          backBtnText="Voltar e votar"
          onBackButton={closeModal}
          onClose={() => {
            closeModal();
            handleEnviarVotos();
          }}
        />
      );
      return;
    }
    handleEnviarVotos();
  }, [limite, categoriasPendentes, showModal, closeModal, handleEnviarVotos]);

  const handleProximaCategoria = useCallback(() => {
    const indiceAtual = CATEGORIAS_PARTICIPANTES_ETAPA3.findIndex(
      (categoria) => categoria.id === categoriaSelecionada
    );
    if (indiceAtual === -1) return;
    const total = CATEGORIAS_PARTICIPANTES_ETAPA3.length;
    for (let offset = 1; offset <= total; offset++) {
      const proxima = CATEGORIAS_PARTICIPANTES_ETAPA3[(indiceAtual + offset) % total];
      const completa = (votos[proxima.id]?.ids?.length || 0) >= limite;
      if (!completa) {
        setCategoriaSelecionada(proxima.id);
        return;
      }
    }
    const proxima = CATEGORIAS_PARTICIPANTES_ETAPA3[(indiceAtual + 1) % total];
    setCategoriaSelecionada(proxima.id);
  }, [categoriaSelecionada, votos, limite]);

  const handleExportarPdf = useCallback(async () => {
    try {
      setIsExportingPdf(true);
      // Importação dinâmica para não adicionar jspdf ao bundle principal se não usado
      const { exportarIniciativasPdf } = await import('utils/votacao-etapa1-export');
      const itensParaExportar = todasIniciativas.map((iniciativa) => ({
        titulo: iniciativa.titulo,
        descricao: iniciativa.descricao || '',
        resultado: iniciativa.resultado || '',
        videoUrl: iniciativa.originalVideoUrl || iniciativa.videoUrl,
        categoria: iniciativa.categoria,
        tema: iniciativa.tema || '',
        categoryPremio: iniciativa.categoryPremio || '',
      }));
      if (itensParaExportar.length > 0) {
        exportarIniciativasPdf(itensParaExportar, 'iniciativas-etapa3-todas-categorias');
      }
    } catch (error) {
      console.error('Erro ao exportar PDF:', error);
    } finally {
      setIsExportingPdf(false);
    }
  }, [todasIniciativas]);

  const abrirTutorialEtapa3 = () => {
    if (!tutorialEtapa3?.steps?.length) return;
    showModal(
      'votacao-etapa3-tutorial-modal',
      <VotacaoEtapa3TutorialModal
        steps={tutorialEtapa3.steps}
        onClose={closeModal}
        onStartVoting={closeModal}
      />,
      'votacao-etapa3-tutorial-wrapper',
      false
    );
  };

  return (
    <div id="votacao-etapa-3-wrapper">
      <HeaderElbb currentPage="inicio" />

      <section className="votacao-etapa-3-hero">
        <div className="votacao-etapa-3-hero__content">
          <h1 className="votacao-etapa-3-hero__title">Etapa 3 de avaliação</h1>
          <p className="votacao-etapa-3-hero__description">
            {limite === 1 ?"Vote na iniciativa que considerar mais relevante para o Prêmio ELBB. " : "Vote nas 3 iniciativas que considerar mais relevante para o Prêmio ELBB."}
{/*
            {limite === 1
              ? ' Selecione 1 iniciativa que considerar mais relevante '
              : ` Selecione as ${limite} iniciativas que considerar mais relevantes `} */}
            <button
              type="button"
              className="votacao-etapa-3-hero__link"
              onClick={abrirTutorialEtapa3}
            >
              Saiba aqui como votar!
            </button>
          </p>

          {perfil === 'assessor' && vpDestino && (
            <p className="votacao-etapa-3-hero__assessor-banner">
              {preliminarSuperseded ? (
                <>O voto final já foi enviado por <strong>{vpDestino.nome}</strong>. Seu voto preliminar foi desconsiderado.</>
              ) : statusPreliminar === 'em_aprovacao' ? (
                <>Voto preliminar enviado para <strong>{vpDestino.nome}</strong> — aguardando a revisão.</>
              ) : preliminarBaseadoNoVp ? (
                <>Você está preparando o voto preliminar para <strong>{vpDestino.nome}</strong>, a partir do rascunho que ele(a) já começou. Editar aqui não altera o rascunho do VP.</>
              ) : (
                <>Você está preparando o voto preliminar para <strong>{vpDestino.nome}</strong>.</>
              )}
            </p>
          )}

          {perfil === 'vp' && preliminarVp && !mostrarModalPreliminar && (
            <p className="votacao-etapa-3-hero__assessor-banner">
              Você aceitou o voto preliminar preparado por{' '}
              <strong>{preliminarVp.assessorNome}</strong>. Revise e envie quando estiver pronto.
            </p>
          )}

          <div className="votacao-etapa-3-hero__timer">
            <span className="votacao-etapa-3-hero__timer-label">
              O PERÍODO DE VOTAÇÃO SERÁ FINALIZADO EM:
            </span>
            <span className="votacao-etapa-3-hero__timer-badge">
              <IoTimeOutline aria-hidden="true" />
              {countdown}
            </span>
          </div>

          <div className="votacao-etapa-3-hero__progress">
            <span className="votacao-etapa-3-hero__progress-date">De {startLabel}</span>
            <div className="votacao-etapa-3-hero__progress-bar">
              <div
                className="votacao-etapa-3-hero__progress-fill"
                style={{ width: `${progresso}%` }}
              />
            </div>
            <span className="votacao-etapa-3-hero__progress-date">Até {endLabel}</span>
          </div>
        </div>
      </section>

      <main className="votacao-etapa-3-main">
        {CATEGORIAS_PARTICIPANTES_ETAPA3.length > 1 && (
          <CategoriasParticipantesEtapa3
            title="Selecione a perspectiva:"
            categoriaSelecionada={categoriaSelecionada}
            onCategoriaChange={setCategoriaSelecionada}
            contadores={contadores}
            limitePorCategoria={limite}
          />
        )}

        <div className="votacao-etapa-3-footer-actions votacao-etapa-3-footer-actions--top">
          <button
            type="button"
            className="votacao-etapa-3-export"
            onClick={handleExportarPdf}
            disabled={isExportingPdf || todasIniciativas.length === 0}
          >
            <IoCloudDownloadOutline size={18} aria-hidden="true" />
            {isExportingPdf ? 'GERANDO PDF...' : 'EXPORTAR PDF'}
          </button>

          {perfil === 'assessor'
            ? !isReadOnly && (
                <button
                  type="button"
                  className="votacao-etapa-3-footer-actions__send"
                  onClick={handleEnviarParaAprovacao}
                  disabled={!prontoParaEnvio || isSubmitting}
                  aria-disabled={!prontoParaEnvio || isSubmitting}
                  title={
                    prontoParaEnvio
                      ? 'Enviar voto preliminar para aprovação do VP'
                      : `Vote em ${limite} iniciativa${limite > 1 ? 's' : ''} em cada perspectiva para habilitar o envio.`
                  }
                >
                  {isSubmitting ? 'ENVIANDO...' : 'ENVIAR PARA APROVAÇÃO'}
                </button>
              )
            : podeEnviar && (
                <button
                  type="button"
                  className="votacao-etapa-3-footer-actions__send"
                  onClick={handleClickEnviar}
                  disabled={!podeClicarEnviar}
                  aria-disabled={!podeClicarEnviar}
                  title={
                    podeClicarEnviar
                      ? 'Enviar votos da Etapa 3'
                      : `Vote em ${limite} iniciativa${limite > 1 ? 's' : ''} em cada perspectiva para habilitar o envio.`
                  }
                >
                  ENVIAR VOTOS
                </button>
              )}
        </div>

        {submitError && (
          <p className="votacao-etapa-3-submit-error" role="alert">
            {submitError}
          </p>
        )}

        {CATEGORIAS_PARTICIPANTES_ETAPA3.length > 1 && (
          <section className="votacao-etapa-3-resumo" aria-live="polite">
            <ul className="votacao-etapa-3-resumo__list">
              {CATEGORIAS_PARTICIPANTES_ETAPA3.map((categoria) => {
                const count = contadores[categoria.id] || 0;
                const completo = count >= limite;
                return (
                  <li
                    key={categoria.id}
                    className={`votacao-etapa-3-resumo__item${completo ? ' is-completed' : ''}`}
                  >
                    <strong>{categoria.nome}</strong>
                    <span> - ({count}/{limite})</span>
                  </li>
                );
              })}
            </ul>
          </section>
        )}

        <section className="votacao-etapa-3-current">
          <div className="votacao-etapa-3-current__card">
            {categoriaInfo && (
              <img
                src={categoriaInfo.imagem}
                alt=""
                aria-hidden="true"
                className="votacao-etapa-3-current__icon"
              />
            )}
            <div className="votacao-etapa-3-current__label">
              <strong>{categoriaInfo?.nome}</strong>
              <span> ({votosCategoriaAtual.length}/{limite} selecionadas)</span>
            </div>
          </div>

          {CATEGORIAS_PARTICIPANTES_ETAPA3.length > 1 && (
            <button
              type="button"
              className="votacao-etapa-3-current__next"
              onClick={handleProximaCategoria}
            >
              PRÓXIMA PERSPECTIVA
            </button>
          )}
        </section>

        <div className="votacao-etapa-3-filters-row">
          <div className="votacao-etapa-3-search">
            <input
              type="search"
              value={busca}
              onChange={(event) => setBusca(event.target.value)}
              placeholder="O que você procura?"
              className="votacao-etapa-3-search__input"
              aria-label="Buscar iniciativa"
            />
            <IoSearch className="votacao-etapa-3-search__icon" aria-hidden="true" />
          </div>
        </div>

        <section className="votacao-etapa-3-list" aria-live="polite">
          {loadingIniciativas && (
            <p className="votacao-etapa-3-list__empty">Carregando iniciativas...</p>
          )}

          {!loadingIniciativas && erroIniciativas && (
            <p className="votacao-etapa-3-list__empty" role="alert">{erroIniciativas}</p>
          )}

          {!loadingIniciativas && !erroIniciativas && iniciativas.length === 0 && (
            <p className="votacao-etapa-3-list__empty">
              Nenhuma iniciativa encontrada.
            </p>
          )}

          {!loadingIniciativas && !erroIniciativas && iniciativas.length > 0 && (
            <div className="votacao-etapa-3-list__grid">
              {iniciativas.map((iniciativa) => {
                const votada = votosCategoriaAtual.includes(iniciativa.id);
                return (
                  <IniciativaCard
                    key={iniciativa.id}
                    {...iniciativa}
                    votar={{
                      votada,
                      onVotar: () => handleToggleVoto(iniciativa.id, iniciativa.titulo, categoriaSelecionada),
                      disabled: isReadOnly || (atingiuLimite && !votada),
                      disabledLabel: isReadOnly
                        ? 'VOTO ENVIADO'
                        : `LIMITE ATINGIDO (${limite}/${limite})`,
                    }}
                  />
                );
              })}

            </div>
          )}
        </section>
      </main>

      {!isReadOnly && ((prontoParaEnvio && limite === 3) ||
        (!floatingDismissed[categoriaSelecionada] && categoriaInfo)) && categoriaInfo && (
          <aside
            className="votacao-etapa-3-floating-card"
            role="status"
            aria-live="polite"
          >
            {prontoParaEnvio && limite === 3 ? (
              <>
                <h4>Parabéns! Você completou seu voto.</h4>
                <p>
                  Agora é só enviar seus votos para concluir a Etapa 3. Você ainda
                  pode revisar antes de enviar.
                </p>
                <div className="votacao-etapa-3-floating-card__actions">
                  <button
                    type="button"
                    className="votacao-etapa-3-floating-card__btn"
                    onClick={perfil === 'assessor' ? handleEnviarParaAprovacao : handleClickEnviar}
                    disabled={isSubmitting}
                  >
                    {isSubmitting
                      ? 'ENVIANDO...'
                      : perfil === 'assessor'
                      ? 'ENVIAR PARA APROVAÇÃO'
                      : 'ENVIAR VOTOS'}
                  </button>
                </div>
              </>
            ) : atingiuLimite ? (
              <>
                <h4>
                  Parabéns! Você votou em {votosCategoriaAtual.length}{' '}
                  iniciativa{votosCategoriaAtual.length === 1 ? '' : 's'}{' '}
                  inspiradora{votosCategoriaAtual.length === 1 ? '' : 's'} do {' '}
                  {categoriaInfo.nome}!
                </h4>
                <p>
                 Caso queira votar em outra iniciativa, será necessário desmarcar a votada anteriormente.
                </p>
                <p className="votacao-etapa-3-floating-card__cta">
                  {limite === 1
                    ? 'Seu voto já está registrado. Você pode editar a qualquer momento.'
                    : CATEGORIAS_PARTICIPANTES_ETAPA3.length > 1
                    ? 'Que tal votar nas outras perspectivas antes de enviar seu voto?'
                    : 'Agora é só enviar seus votos para concluir a Etapa 3.'}
                </p>
                <div className="votacao-etapa-3-floating-card__actions">
                  {CATEGORIAS_PARTICIPANTES_ETAPA3.length > 1 && (
                    <button
                      type="button"
                      className="votacao-etapa-3-floating-card__btn votacao-etapa-3-floating-card__btn--ghost"
                      onClick={() => {
                        setFloatingDismissed((prev) => ({
                          ...prev,
                          [categoriaSelecionada]: true,
                        }));
                        handleProximaCategoria();
                      }}
                    >
                      PRÓXIMA PERSPECTIVA
                    </button>
                  )}
                  {(limite === 1 || CATEGORIAS_PARTICIPANTES_ETAPA3.length <= 1) && (
                    <button
                      type="button"
                      className="votacao-etapa-3-floating-card__btn"
                      onClick={handleClickEnviar}
                      disabled={isSubmitting}
                    >
                      {isSubmitting ? 'ENVIANDO...' : limite === 1 ? 'ENVIAR VOTO' : 'ENVIAR VOTOS'}
                    </button>
                  )}
                </div>
              </>
            ) : (
              (() => {
                const restantes = limite - votosCategoriaAtual.length;
                const plural = restantes === 1 ? '' : 's';
                return (
                  <>
                    <h4>
                      Você ainda não votou em {restantes} iniciativa{plural}{' '}
                      inspiradora{plural} do {categoriaInfo.nome}!
                    </h4>
                    <p>
                      É necessário votar em{' '}
                      <span className="votacao-etapa-3-floating-card__highlight">
                        {restantes} iniciativa{plural}
                      </span>{' '}
                      para salvar o seu voto.
                    </p>
                  </>
                );
              })()
            )}
          </aside>
        )}
    </div>
  );
}
